import { useState } from 'react';
type UploadProgress = {
  fileName: string;
  progress: number;
  error?: string;
  completed: boolean;
};
export const useUploadTracker = () => {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [hasGlobalError, setHasGlobalError] = useState(false);
  const [activeRequests, setActiveRequests] = useState<XMLHttpRequest[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const resetUploads = () => {
    setUploads([]);
    setTotalProgress(0);
    setHasGlobalError(false);
    abortAllRequests();
    setIsUploading(false);
  };

  const abortAllRequests = () => {
    activeRequests.forEach(xhr => {
      if (xhr && xhr.readyState !== XMLHttpRequest.DONE) {
        xhr.abort();
      }
    });
    setActiveRequests([]);
  };

  const addActiveRequest = (xhr: XMLHttpRequest) => {
    setActiveRequests(prev => [...prev, xhr]);
  };

  const removeActiveRequest = (xhr: XMLHttpRequest) => {
    setActiveRequests(prev => prev.filter(req => req !== xhr));
  };

  const initializeUploads = (files: File[]) => {
    setUploads(files.map(file => ({
      fileName: file.name,
      progress: 0,
      completed: false,
      error: undefined
    })));
    setHasGlobalError(false);
    setIsUploading(true);
  };

  const updateProgress = (fileName: string, progress: number, error?: string) => {
    setUploads(prev => {
      const newUploads = prev.map(upload =>
        upload.fileName === fileName
          ? { ...upload, progress, error, completed: progress === 100 }
          : upload
      );

      if (error && error.includes('conflict')) {
        setHasGlobalError(true);
        return newUploads.map(u => ({
          ...u,
          error: 'Upload conflict detected'
        }));
      }

      if (hasGlobalError) {
        return newUploads.map(u => ({
          ...u,
          error: 'Upload conflict detected'
        }));
      }

      return newUploads;
    });

    const total = uploads.reduce((sum, u) => sum + u.progress, 0) / Math.max(uploads.length, 1);
    setTotalProgress(total);
  };

  return {
    uploads,
    totalProgress,
    hasGlobalError,
    activeRequests,
    initializeUploads,
    updateProgress,
    resetUploads,
    abortAllRequests,
    isUploading,
    addActiveRequest,
    removeActiveRequest
  };
};
